import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DataPagination, Skeleton, Text } from "@musa.tech/design-system";
import { ColetaCalendarCard } from "./ColetaCalendarCard";
import type { CollectForTable } from "@/hooks/useCollectsApiData";

interface AgendaViewProps {
  byDay: Map<string, CollectForTable[]>;
  isLoading: boolean;
  onSelect?: (collect: CollectForTable) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

export const AgendaView = ({
  byDay,
  isLoading,
  onSelect,
  page,
  pageSize,
  total,
  onPageChange,
}: AgendaViewProps) => {
  const days = useMemo(
    () => Array.from(byDay.keys()).sort((a, b) => a.localeCompare(b)),
    [byDay],
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (days.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-white p-8 text-center">
        <Text variant="body" className="text-neutral-500">
          Sem próximas coletas.
        </Text>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {days.map((dayK) => {
        const collects = byDay.get(dayK) ?? [];
        const date = parseISO(`${dayK}T00:00:00`);
        return (
          <div
            key={dayK}
            className="overflow-hidden rounded-lg border border-border bg-white"
          >
            <div className="border-b border-border bg-neutral-100 px-4 py-2">
              <Text variant="label" className="text-neutral-800">
                {capitalize(
                  format(date, "EEEE, dd 'de' MMMM", { locale: ptBR }),
                )}
              </Text>
              <Text
                variant="body"
                size="small"
                className="block text-neutral-500"
              >
                {collects.length}{" "}
                {collects.length === 1 ? "coleta" : "coletas"}
              </Text>
            </div>
            <div className="divide-y divide-border">
              {collects.map((collect) => (
                <ColetaCalendarCard
                  key={collect.collect_id}
                  collect={collect}
                  layout="row"
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
        );
      })}

      {total > pageSize && (
        <DataPagination
          page={page}
          pageSize={pageSize}
          total={total}
          onChange={onPageChange}
        />
      )}
    </div>
  );
};
